// useBeneAIConversation — BeneAI-01.
//
// Send-message hook that:
//   * builds the canonical user-message row optimistically
//   * posts ``/conversations/{id}/messages``
//   * appends the assistant message returned by the backend
//   * surfaces typed errors so the drawer can render specific copy
//
// One-active-conversation model: when ``conversationId`` is ``null``
// (drawer just opened on a fresh chat) the backend mints a new
// session and the response's ``conversation_id`` is committed back
// into the drawer state so subsequent messages append to the same
// session.

import { useCallback, useState } from "react";

import { ApiError } from "@/api/client";

import { sendMessage } from "./api";
import { useBeneAIDrawer } from "./BeneAIDrawerContext";
import type {
  BeneAIContext, BeneAIErrorCode, BeneAIMessage,
} from "./types";


interface SendOptions {
  /** Override the temporary message_id used for the optimistic user
   *  bubble (tests use this for deterministic IDs). */
  optimisticUserId?: string;
}


interface SendResult {
  ok: boolean;
  error?: { code: BeneAIErrorCode; message: string };
}


export interface UseBeneAIConversation {
  isSending: boolean;
  lastError: { code: BeneAIErrorCode; message: string } | null;
  send: (
    body: string, context: BeneAIContext, opts?: SendOptions,
  ) => Promise<SendResult>;
}


export function useBeneAIConversation(): UseBeneAIConversation {
  const drawer = useBeneAIDrawer();
  const [isSending, setIsSending] = useState(false);
  const [lastError, setLastError] = useState<
    { code: BeneAIErrorCode; message: string } | null
  >(null);

  const send = useCallback<UseBeneAIConversation["send"]>(
    async (body, context, opts) => {
      const trimmed = body.trim();
      if (trimmed.length === 0) {
        return {
          ok: false,
          error: {
            code: "message_required",
            message: "Message can't be empty.",
          },
        };
      }
      setIsSending(true);
      setLastError(null);

      // Optimistic user bubble — sequence -1 keeps it visually after
      // any historical messages while real sequence numbers (>= 1)
      // come back from the server. The ``message_id`` is overwritten
      // when the canonical row lands.
      const placeholderId =
        opts?.optimisticUserId ?? `pending-${Date.now()}`;
      const optimistic: BeneAIMessage = {
        message_id: placeholderId,
        conversation_id: drawer.conversationId ?? "pending",
        role: "user",
        content: trimmed,
        content_json: { context },
        sequence: -1,
        created_at: new Date().toISOString(),
      };
      drawer.appendMessage(optimistic);

      try {
        const resp = await sendMessage(
          drawer.conversationId,
          { message: trimmed, context },
        );
        if (drawer.conversationId === null) {
          drawer.setConversationId(resp.conversation_id);
        }
        drawer.appendMessage(resp.assistant_message);
        if (
          resp.pending_confirmations
          && resp.pending_confirmations.length > 0
        ) {
          drawer.registerPendingConfirmations(resp.pending_confirmations);
        }
        return { ok: true };
      } catch (err) {
        const parsed = parseSendError(err);
        setLastError(parsed);
        // Append a synthetic assistant row so the failure is visible
        // inline in the transcript instead of just a composer footer.
        // parseAssistantContent detects ``content_json.kind === "error"``
        // and emits an error block; the renderer shows a rose-tone
        // bubble. The row is local-only — it lives in drawer state but
        // is not persisted by the backend, so refreshing the
        // conversation drops it (the loop didn't write an assistant
        // AgentMessage on the error path).
        drawer.appendMessage({
          message_id: `error-${Date.now()}`,
          conversation_id: drawer.conversationId ?? "pending",
          role: "assistant",
          content: parsed.message,
          content_json: {
            kind: "error",
            error_code: parsed.code,
            message: parsed.message,
          },
          sequence: -1,
          created_at: new Date().toISOString(),
        });
        return { ok: false, error: parsed };
      } finally {
        setIsSending(false);
      }
    },
    [drawer],
  );

  return { isSending, lastError, send };
}


function parseSendError(err: unknown): {
  code: BeneAIErrorCode; message: string;
} {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as Record<string, unknown>;
    const detail = typeof body.detail === "string" ? body.detail : null;
    const code = (detail as BeneAIErrorCode) ?? "unknown";
    const message = (typeof body.message === "string"
      ? body.message
      : err.message);
    return { code, message };
  }
  if (err instanceof Error) {
    return { code: "unknown", message: err.message };
  }
  return { code: "unknown", message: "Something went wrong." };
}
